import { useId, useLayoutEffect, useRef, useState } from "react";
import { House, MessagesSquare, Pause, Play, UserRound } from "lucide-react";

/*
 * Shared pieces for the homepage frames: brand colours, the cloth texture,
 * a phone drawn in code (so its screen is the real app, always sharp), and
 * the pause button every moving frame carries.
 */

export const C = {
  INK: "#0A0F0E",
  BRAND_DARK: "#00332F",
  TEAL: "#2EE6D6",
  TEAL_DEEP: "#00786F",
  TEAL_SOFT: "#CFF9F4",
  OAK: "#C6872F",
  OAK_SOFT: "#F6E7D2",
  SECONDARY: "#46504D",
  MUTED: "#69716E",
  PANEL: "#F2F2ED",
  RULE: "#E0E3DE",
} as const;

/** The phone is drawn at this size and scaled to fit its frame. */
const PHONE_W = 300;
const PHONE_H = 620;

/** Scallops, like the cloth on Wise's boxes, in our colours. */
export function Texture({ color }: { color: string }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <pattern id={id} width="56" height="56" patternUnits="userSpaceOnUse">
          <circle cx="28" cy="56" r="27" fill="none" stroke={color} strokeWidth="2.5" />
          <circle cx="0" cy="28" r="27" fill="none" stroke={color} strokeWidth="2.5" />
          <circle cx="56" cy="28" r="27" fill="none" stroke={color} strokeWidth="2.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

function StatusBar() {
  return (
    <div className="absolute inset-x-0 top-0 z-30 flex h-[48px] items-center justify-between px-[26px] pt-1 text-[13px] font-semibold" style={{ color: C.INK }}>
      <span className="tabular-nums">9:41</span>
      <span className="flex items-center gap-[5px]" aria-hidden="true">
        <span className="flex items-end gap-[1.5px]">
          {[4, 6, 8, 10].map((h) => (
            <span key={h} className="w-[3px] rounded-[1px]" style={{ height: h, backgroundColor: C.INK }} />
          ))}
        </span>
        <svg width="15" height="11" viewBox="0 0 15 11">
          <path d="M7.5 2.2c2.1 0 4 .8 5.5 2.1l1-1A9 9 0 0 0 7.5.8 9 9 0 0 0 1 3.3l1 1a7.6 7.6 0 0 1 5.5-2.1Zm0 3c1.3 0 2.4.5 3.3 1.2l1-1a6 6 0 0 0-8.6 0l1 1c.9-.7 2-1.2 3.3-1.2Zm0 3a2 2 0 0 0-1.3.5L7.5 10l1.3-1.3a2 2 0 0 0-1.3-.5Z" fill={C.INK} />
        </svg>
        <span className="relative h-[11px] w-[22px] rounded-[3px] border" style={{ borderColor: C.INK }}>
          <span className="absolute inset-[1.5px] right-[5px] rounded-[1.5px]" style={{ backgroundColor: C.INK }} />
        </span>
      </span>
    </div>
  );
}

function TabBar({ active }: { active: "home" | "community" | "account" }) {
  const tabs = [
    { id: "home", label: "Home", Icon: House },
    { id: "community", label: "Community", Icon: MessagesSquare },
    { id: "account", label: "Account", Icon: UserRound },
  ] as const;
  return (
    <div className="absolute inset-x-0 bottom-0 z-20 border-t bg-white px-6 pb-[22px] pt-2" style={{ borderColor: C.RULE }}>
      <div className="flex justify-between">
        {tabs.map(({ id, label, Icon }) => (
          <span key={id} className="flex w-16 flex-col items-center gap-0.5 text-[10.5px] font-semibold" style={{ color: id === active ? C.INK : C.MUTED }}>
            <Icon className="h-[19px] w-[19px]" strokeWidth={id === active ? 2.4 : 1.9} />
            {label}
          </span>
        ))}
      </div>
      <span className="mx-auto mt-2 block h-[4px] w-[110px] rounded-full" style={{ backgroundColor: C.INK }} />
    </div>
  );
}

/** A phone, drawn at 300 × 620 and scaled to the width of its slot. */
export function Phone({ children, tab = "home", tilt = 0, width = "58%" }: { children: React.ReactNode; tab?: "home" | "community" | "account"; tilt?: number; width?: string }) {
  const slot = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.8);
  useLayoutEffect(() => {
    const el = slot.current;
    if (!el) return;
    const measure = () => setScale(el.clientWidth / PHONE_W);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={slot} className="relative" style={{ width, aspectRatio: `${PHONE_W} / ${PHONE_H}`, transform: `rotate(${tilt}deg)` }}>
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: PHONE_W,
          height: PHONE_H,
          transform: `scale(${scale})`,
          borderRadius: 48,
          padding: 9,
          background: "linear-gradient(145deg, #2A2F2E 0%, #0A0F0E 40%, #1A1F1E 100%)",
          boxShadow:
            "0 1px 0 1px rgba(255,255,255,0.06) inset, 0 50px 70px -20px rgba(10,15,14,0.45), 0 24px 30px -12px rgba(10,15,14,0.3), 0 2px 4px rgba(10,15,14,0.2)",
        }}
      >
        <div className="relative h-full w-full overflow-hidden bg-white" style={{ borderRadius: 39 }}>
          <span className="absolute left-1/2 top-[11px] z-40 h-[27px] w-[88px] -translate-x-1/2 rounded-full bg-black" aria-hidden="true" />
          <StatusBar />
          <div className="absolute inset-0 pb-[74px] pt-[52px]">{children}</div>
          <TabBar active={tab} />
        </div>
      </div>
    </div>
  );
}

export function PauseButton({ paused, onToggle, hidden }: { paused: boolean; onToggle: () => void; hidden: boolean }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-[4.5%] top-[3.6%] z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow-[0_2px_10px_rgba(10,15,14,0.12)] transition-transform hover:scale-105"
      aria-label={paused ? "Play animation" : "Pause animation"}
      hidden={hidden}
    >
      {paused ? <Play className="h-4 w-4 translate-x-[1px]" style={{ color: C.INK }} fill={C.INK} /> : <Pause className="h-4 w-4" style={{ color: C.INK }} fill={C.INK} />}
    </button>
  );
}

export interface SceneChip {
  icon: React.ReactNode;
  label: string;
  value: string;
}

